import { fadeColor, lighten } from '@precis-digital/kurama/utils';
import { type ThemeColor, type Theme } from '@precis-digital/kurama/theme';
import { type ThemeMode } from '@precis-digital/kurama/AppProvider';
import { type RefObject } from 'react';
import { makeErrorToast, makeSuccessToast } from 'shared/components/Toaster';
import { sendGTMEvent } from 'shared/googleTagManager';
import ActiveDot from '../components/ActiveDot';
import { CHART_CARD_CHART_MARGIN, CHART_CARD_EXPORT_LAYOUT, linePatterns } from '../constants';
import type { ChartCardDefaults, ChartCardTFunction, ChartChoiceOptions, ChartDataRow, ChartSelections, ChartType, DefaultDrawingInnerProps, DrawingColorMaps, DrawingInnerProps, LinePattern, PolarChartDefinition, ScatterChartDefinition, StandardChartType } from '../types';
import type { AxisIdProps, ChartAxisIdsAndDomains, ChartChoice, ChartMarginsAndAxisSpaces, DrawingElementProps, ElementsDescriptionsMap, FirstOrThirdAxisDetails, MetricDomain, SecondAxisChoiceValuesType, SecondAxisDetails, SecondAxisTypes } from '../internalTypes';
import { createExportLegendsSVGItems, generateChoicesExportLegendItems } from './exportLegend';
import { assertNever, errorLocal, isSelectionOthersId, objectEntries } from './generics';
import { addLogoToSVG, createSVGTextItem, getCanvasContext, handleFontInSVGString } from './graph';
export const getSelectedOtherOptions = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(choiceValues: SecondAxisChoiceValuesType<DataKey, DataRow> | undefined, selectedValues: string[] | undefined, otherSelected: DataKey | undefined, selectedOthers: boolean | undefined): ChartChoiceOptions<string> | undefined => {
  if (selectedOthers === true) {
    if (choiceValues == null || selectedValues == null) {
      errorLocal(`getSelectedOtherOptions selectedOthers true and choiceValues or selectedValues null`, {
        choiceValues,
        selectedValues
      });
      return;
    }
    let options: ChartChoiceOptions<string> | undefined;
    if (choiceValues.type === 'valuesOf') {
      if (otherSelected == null) {
        errorLocal(`getSelectedOtherOptions choiceValues valuesOf and otherSelected null`, {
          choiceValues,
          selectedValues
        });
        return;
      }
      options = choiceValues.change.options[otherSelected];
    } else {
      options = choiceValues.change.options;
    }
    return options.filter(({
      value
    }) => !selectedValues.includes(value));
  }
};
export const getSecondAxisDomain = (axisType: SecondAxisTypes, chartAxisIdsAndDomains: ChartAxisIdsAndDomains): MetricDomain | undefined => {
  switch (axisType) {
    case 'second':
      return chartAxisIdsAndDomains.secondAxisDomain;
    case 'otherSecond':
      return chartAxisIdsAndDomains.otherSecondAxisDomain;
  }
};
const pushDrawingProps = <DataKey extends string,>({
  chartType,
  customProps,
  axisIdProps,
  color,
  pattern,
  dataKey,
  axisType,
  chartDrawing,
  drawingColorMaps,
  drawingProps
}: {
  chartType: StandardChartType;
  customProps: Omit<DrawingInnerProps<ChartType>, 'dataKey'> | undefined;
  axisIdProps: AxisIdProps;
  color: ThemeColor | undefined;
  pattern: LinePattern | undefined;
  dataKey: DataKey;
  axisType: SecondAxisTypes;
  chartDrawing: DefaultDrawingInnerProps;
  drawingColorMaps: DrawingColorMaps;
  drawingProps: DrawingElementProps[];
}): void => {
  const props: DrawingInnerProps<typeof chartType> = {
    dataKey,
    ...chartDrawing[chartType]
  };
  if (chartType === 'line') {
    props.strokeDasharray = linePatterns[pattern ?? 'continuous'];
  }
  Object.assign(props, {
    ...customProps,
    ...axisIdProps,
    animationActive: false
  });
  if (color != null) {
    const secondary = drawingColorMaps.secondary[chartType];
    const primary = drawingColorMaps.primary[chartType];
    props[primary] = color;
    if (secondary != null) {
      if (chartType === 'area') {
        props[secondary] = lighten(color, 0.3);
      } else {
        props[secondary] = color;
      }
    }
    if (chartType === 'line' || chartType === 'area') {
      const lineAreaProps = props as DrawingInnerProps<'line' | 'area'>;
      lineAreaProps.activeDot ??= <ActiveDot fill={color} />;
    }
  }
  drawingProps.push({
    key: dataKey,
    type: chartType,
    axisType,
    props
  });
};
const pushOthersValuesDrawingProps = <DataKey extends string,>({
  dataKey,
  axisType,
  respectColors,
  color,
  pattern,
  otherOptions,
  customProps,
  axisIdProps,
  chartType,
  chartDrawing,
  drawingColorMaps,
  drawingProps
}: {
  dataKey: DataKey;
  axisType: SecondAxisTypes;
  respectColors: boolean;
  color: ThemeColor | undefined;
  pattern: LinePattern | undefined;
  otherOptions: ChartChoiceOptions<string> | undefined;
  customProps: Omit<DrawingInnerProps<ChartType>, 'dataKey'> | undefined;
  axisIdProps: AxisIdProps;
  chartType: StandardChartType;
  chartDrawing: DefaultDrawingInnerProps;
  drawingColorMaps: DrawingColorMaps;
  drawingProps: DrawingElementProps[];
}): void => {
  if (otherOptions == null) {
    errorLocal(`getSecondAxisDrawingElementsProps selectedOthers true and otherOptions null`);
    return;
  }
  pushDrawingProps({
    chartType,
    customProps,
    axisIdProps,
    color: respectColors ? otherOptions[0].color ?? color : color ?? otherOptions[0].color,
    pattern: respectColors ? otherOptions[0].pattern ?? pattern : pattern ?? otherOptions[0].pattern,
    dataKey,
    axisType,
    chartDrawing,
    drawingColorMaps,
    drawingProps
  });
};
const pushStandardValuesDrawingProps = <DataKey extends string,>({
  dataKey,
  axisType,
  respectColors,
  color,
  pattern,
  elementsDescriptionsMap,
  customProps,
  axisIdProps,
  chartType,
  chartDrawing,
  drawingColorMaps,
  drawingProps
}: {
  dataKey: DataKey;
  axisType: SecondAxisTypes;
  respectColors: boolean;
  color: ThemeColor | undefined;
  pattern: LinePattern | undefined;
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
  customProps: Omit<DrawingInnerProps<ChartType>, 'dataKey'> | undefined;
  axisIdProps: AxisIdProps;
  chartType: StandardChartType;
  chartDrawing: DefaultDrawingInnerProps;
  drawingColorMaps: DrawingColorMaps;
  drawingProps: DrawingElementProps[];
}): void => {
  const elementDescription = elementsDescriptionsMap[dataKey];
  if (elementDescription == null) {
    errorLocal(`getSecondAxisDrawingElementsProps ${dataKey} elementDescription null`, {
      elementsDescriptionsMap,
      dataKey
    });
    return;
  }
  pushDrawingProps({
    chartType,
    customProps,
    axisIdProps,
    color: respectColors ? elementDescription.color ?? color : color ?? elementDescription.color,
    pattern: respectColors ? elementDescription.pattern ?? pattern : pattern ?? elementDescription.pattern,
    dataKey,
    axisType,
    chartDrawing,
    drawingColorMaps,
    drawingProps
  });
};
export const createCartesianDrawingElementsProps = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  chartType,
  respectColors,
  color,
  pattern,
  customProps,
  axisIdProps,
  choiceValues,
  selectedValues,
  otherSelected,
  selectedOthers,
  dataKeys,
  axisType,
  elementsDescriptionsMap,
  chartDrawing,
  drawingColorMaps,
  drawingProps
}: {
  chartType: StandardChartType;
  respectColors: boolean;
  color: ThemeColor | undefined;
  pattern: LinePattern | undefined;
  customProps: Omit<DrawingInnerProps<ChartType>, 'dataKey'> | undefined;
  axisIdProps: AxisIdProps;
  choiceValues: SecondAxisChoiceValuesType<DataKey, DataRow> | undefined;
  selectedValues: string[] | undefined;
  otherSelected: DataKey | undefined;
  selectedOthers: boolean | undefined;
  dataKeys: DataKey[];
  axisType: SecondAxisTypes;
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
  chartDrawing: DefaultDrawingInnerProps;
  drawingColorMaps: DrawingColorMaps;
  drawingProps: DrawingElementProps[];
}): void => {
  const otherOptions = getSelectedOtherOptions(choiceValues, selectedValues, otherSelected, selectedOthers);
  if (selectedOthers === true && otherOptions == null) {
    errorLocal(`getSecondAxisDrawingElementsProps selectedOthers true and otherOptions null`, {
      selectedOthers,
      choiceValues,
      selectedValues
    });
    return;
  }
  for (const dataKey of dataKeys) {
    if (selectedOthers === true && isSelectionOthersId(dataKey)) {
      pushOthersValuesDrawingProps({
        dataKey,
        axisType,
        respectColors,
        color,
        pattern,
        otherOptions,
        customProps,
        axisIdProps,
        chartType,
        chartDrawing,
        drawingColorMaps,
        drawingProps
      });
    } else {
      pushStandardValuesDrawingProps({
        dataKey,
        axisType,
        respectColors,
        color,
        pattern,
        elementsDescriptionsMap,
        customProps,
        axisIdProps,
        chartType,
        chartDrawing,
        drawingColorMaps,
        drawingProps
      });
    }
  }
};
export const createScatterDrawingElementsProps = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  axisProps,
  elementsDescriptionsMap,
  dimensionAxisDetails,
  secondAxisDetails,
  chartDrawing,
  drawingProps
}: {
  axisProps: ScatterChartDefinition<DataKey, DataRow>;
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
  dimensionAxisDetails: FirstOrThirdAxisDetails<DataKey, DataRow>;
  secondAxisDetails: Array<SecondAxisDetails<DataKey, DataRow>>;
  respectColors: boolean;
  chartDrawing: DefaultDrawingInnerProps;
  drawingColorMaps: DrawingColorMaps;
  drawingProps: DrawingElementProps[];
}): void => {
  const {
    layout,
    thirdAxis,
    customProps
  } = axisProps;
  const {
    axis
  } = secondAxisDetails[0];
  const axisIdProps: AxisIdProps = layout === 'vertical' ? {
    xAxisId: axis
  } : {
    yAxisId: axis
  };
  const elementDescription = elementsDescriptionsMap[thirdAxis];
  if (elementDescription == null) {
    errorLocal(`getSecondAxisDrawingElementsProps ${thirdAxis} elementDescription null`, {
      elementsDescriptionsMap,
      thirdAxis,
      axisProps
    });
    return;
  }
  const {
    descriptions,
    color
  } = elementDescription;
  const colorMap: Record<string, string> | undefined = descriptions == null ? undefined : Object.fromEntries(objectEntries(descriptions).map(([value, description]) => [value, description.color]).filter(([_, color]) => color != null));
  drawingProps.push({
    key: dimensionAxisDetails.dataKey,
    type: 'scatter',
    axisType: 'third',
    props: {
      ...customProps,
      ...axisIdProps,
      ...chartDrawing.scatter,
      isAnimationActive: false,
      colorMap,
      fill: color,
      dataKey: dimensionAxisDetails.dataKey
    }
  });
};
export const createPolarDrawingElementsProps = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  axisProps,
  dimensionAxisDetails,
  secondAxisDetails,
  elementsDescriptionsMap,
  chartDrawing,
  drawingProps
}: {
  axisProps: PolarChartDefinition<DataKey, DataRow>;
  dimensionAxisDetails: FirstOrThirdAxisDetails<DataKey, DataRow>;
  secondAxisDetails: Array<SecondAxisDetails<DataKey, DataRow>>;
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
  respectColors: boolean;
  chartDrawing: DefaultDrawingInnerProps;
  drawingColorMaps: DrawingColorMaps;
  drawingProps: DrawingElementProps[];
}): void => {
  const {
    type: chartType,
    firstAxis,
    customProps
  } = axisProps;
  const elementDescription = elementsDescriptionsMap[firstAxis];
  if (elementDescription == null) {
    errorLocal(`getSecondAxisDrawingElementsProps ${firstAxis} elementDescription null`, {
      elementsDescriptionsMap,
      firstAxis,
      axisProps
    });
    return;
  }
  const {
    descriptions
  } = elementDescription;
  const colorMap: Record<string, string> | undefined = descriptions == null ? undefined : Object.fromEntries(objectEntries(descriptions).map(([value, description]) => [value, description.color]).filter(([_, color]) => color != null));
  for (const {
    dataKeys,
    axisType
  } of secondAxisDetails) {
    for (const dataKey of dataKeys) {
      drawingProps.push({
        key: dimensionAxisDetails.axis,
        type: chartType,
        axisType,
        props: {
          dataKey,
          nameKey: dimensionAxisDetails.dataKey,
          ...chartDrawing[chartType],
          ...customProps,
          isAnimationActive: false,
          colorMap
        }
      });
    }
  }
};
const exportAsSVG = (svgString: string, downloadFileName: string, copyToClipboard: boolean, t: ChartCardTFunction): void => {
  // Copy to clipboard instead of downloading
  if (copyToClipboard) {
    void navigator.clipboard.writeText(svgString).then(() => {
      makeSuccessToast(t('svgCopiedToClipboard'));
    }).catch(error => {
      errorLocal('Failed to copy SVG to clipboard:', error);
      makeErrorToast(t('failedToCopySVG'));
    });
    return;
  }

  // Create and download SVG file
  const blob = new Blob([svgString], {
    type: 'image/svg+xml;charset=utf-8'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${downloadFileName}.svg`;

  // Download
  link.click();

  // Clean up
  URL.revokeObjectURL(url);
};
const handleSvgToImg = ({
  svgUrl,
  img,
  context,
  width,
  height,
  canvas,
  downloadFileName,
  copyToClipboard,
  t,
  resolve
}: {
  svgUrl: string;
  img: HTMLImageElement;
  context: CanvasRenderingContext2D;
  width: number;
  height: number;
  canvas: HTMLCanvasElement;
  downloadFileName: string;
  copyToClipboard: boolean;
  t: ChartCardTFunction;
  resolve: () => void;
}): void => {
  img.onload = () => {
    // Draw SVG to canvas
    context.drawImage(img, 0, 0, width, height);

    // Convert to PNG and download
    canvas.toBlob(blob => {
      if (blob == null) {
        errorLocal('Failed to convert SVG to PNG: Blob is null!');
        makeErrorToast(t('failedToGeneratePNG'));
        return;
      }

      // Copy to clipboard instead of downloading
      if (copyToClipboard) {
        void navigator.clipboard.write([new ClipboardItem({
          'image/png': blob
        })]).then(() => {
          makeSuccessToast(t('pngCopiedToClipboard'));
          resolve();
        }).catch(error => {
          errorLocal('Failed to copy PNG to clipboard:', error);
          makeErrorToast(t('failedToCopyPNG'));
          resolve();
        });
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${downloadFileName}.png`;

      // Download
      link.click();

      // Clean up
      URL.revokeObjectURL(url);
      URL.revokeObjectURL(svgUrl);
      resolve();
    }, 'image/png');
  };
  img.onerror = event => {
    errorLocal('Failed to convert SVG to PNG: Image onerror!', event);
    makeErrorToast(t('failedToGeneratePNG'));
    URL.revokeObjectURL(svgUrl);
  };
  img.src = svgUrl;
};
const exportAsPNG = async ({
  svgString,
  width,
  height,
  downloadFileName,
  copyToClipboard,
  t,
  canvas,
  context
}: {
  svgString: string;
  width: number;
  height: number;
  downloadFileName: string;
  copyToClipboard: boolean;
  t: ChartCardTFunction;
  canvas: HTMLCanvasElement;
  context: CanvasRenderingContext2D;
}): Promise<void> => {
  // Resize canvas
  canvas.width = width * CHART_CARD_EXPORT_LAYOUT.scalePNG;
  canvas.height = height * CHART_CARD_EXPORT_LAYOUT.scalePNG;

  // Scale the context
  context.scale(CHART_CARD_EXPORT_LAYOUT.scalePNG, CHART_CARD_EXPORT_LAYOUT.scalePNG);
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);

  // Create image from SVG
  const img = new Image();

  // Convert SVG to data URL
  const svgBlob = new Blob([svgString], {
    type: 'image/svg+xml;charset=utf-8'
  });
  const svgUrl = URL.createObjectURL(svgBlob);
  await new Promise<void>(resolve => {
    handleSvgToImg({
      svgUrl,
      img,
      context,
      width,
      height,
      canvas,
      downloadFileName,
      copyToClipboard,
      t,
      resolve
    });
  });
};
export const exportChartAsSVGOrPNG = async <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(props: {
  defaults: ChartCardDefaults;
  theme: Theme;
  choicesList: DataKey[];
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>;
  choicesTogether?: boolean;
  transposedMetricsLists: Map<DataKey, Set<DataKey>>;
  selections: ChartSelections<DataKey>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  chartMarginRef: RefObject<ChartMarginsAndAxisSpaces>;
  title: string;
  downloadFileName: string;
  t: ChartCardTFunction;
  exportAs: 'svg' | 'png';
  copyToClipboard: boolean;
}): Promise<void> => {
  const {
    defaults,
    theme,
    choicesList,
    choicesMap,
    transposedMetricsLists,
    selections,
    containerRef,
    chartMarginRef,
    title,
    downloadFileName,
    t,
    exportAs,
    copyToClipboard
  } = props;
  if (containerRef.current == null) {
    return;
  }
  const choicesTogether = props.choicesTogether === true;
  const chartMarginsAndAxisSpaces: ChartMarginsAndAxisSpaces = chartMarginRef.current;

  // Generate legend items
  const {
    singleLegendItems,
    chipsLegendItems,
    togetherLegendItems
  } = generateChoicesExportLegendItems<DataKey, DataRow>({
    choicesList,
    choicesMap,
    choicesTogether,
    transposedMetricsLists,
    selections,
    t
  });

  // Get fonts for embedding in SVG for proper rendering in exports
  const {
    getEmbeddedFontStyle
  } = await import('./embeddedFonts');
  const embeddedFontStyle = await getEmbeddedFontStyle();

  // If unable to use desired fonts, use fallback ones for rendering
  const fallbackFontFamily = exportAs === 'png' ? CHART_CARD_EXPORT_LAYOUT.fallbackFontFamilyPNG : CHART_CARD_EXPORT_LAYOUT.fallbackFontFamilySVG;
  const {
    context,
    canvas
  } = getCanvasContext();
  if (context == null) {
    errorLocal('Canvas context not found!');
    makeErrorToast('Failed to build canvas');
    return;
  }

  // Find the SVG element inside the chart's container
  const svg = containerRef.current.querySelector('svg');
  if (svg == null) {
    errorLocal('SVG not found!');
    if (exportAs === 'svg') {
      makeErrorToast(t('failedToGenerateSVG'));
    } else {
      makeErrorToast(t('failedToGeneratePNG'));
    }
    return;
  }

  // Clone the SVG to avoid modifying the original DOM
  const svgClone = svg.cloneNode(true) as SVGSVGElement;

  // Get current SVG dimensions
  const originalWidth = Number.parseFloat(svgClone.getAttribute('width') ?? '0');
  const originalHeight = Number.parseFloat(svgClone.getAttribute('height') ?? '0');
  const leftMargin = chartMarginsAndAxisSpaces.margin.left;
  const rightMargin = chartMarginsAndAxisSpaces.margin.right + chartMarginsAndAxisSpaces.secondAxisSpace;
  let yStart = 0;
  const xStart = leftMargin;

  // Create header group
  const headerGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  headerGroup.setAttribute('class', 'chart-header');
  const {
    yOffset: titleYOffset,
    height: titleHeight
  } = createSVGTextItem<DataKey>({
    text: title,
    asLabel: false,
    xOffset: leftMargin,
    context,
    container: headerGroup,
    fontProps: defaults.fonts.title
  });
  yStart = await addLogoToSVG({
    svgWidth: originalWidth,
    rightMargin,
    y: yStart,
    logoHeight: titleHeight * 1.5,
    minYOffset: titleYOffset,
    bottomPadding: CHART_CARD_EXPORT_LAYOUT.headerBottomPadding,
    container: headerGroup
  });

  // Create legend group
  const legendGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  legendGroup.setAttribute('class', 'chart-legend');
  const innerWidth = originalWidth - xStart - rightMargin - chartMarginsAndAxisSpaces.secondAxisSpace;

  // Add legend items
  const yPosition = createExportLegendsSVGItems<DataKey>({
    legendGroup,
    singleLegendItems,
    chipsLegendItems,
    togetherLegendItems,
    yStart,
    xStart,
    width: innerWidth,
    context,
    defaults,
    theme
  });

  // 6. Wrap all existing content in a group and shift it down
  const contentGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  contentGroup.setAttribute('transform', `translate(0, ${yPosition})`);

  // Move all children to the content group
  while (svgClone.firstChild != null) {
    contentGroup.appendChild(svgClone.firstChild);
  }
  svgClone.appendChild(headerGroup);
  svgClone.appendChild(legendGroup);
  svgClone.appendChild(contentGroup);
  const width = originalWidth + CHART_CARD_CHART_MARGIN.left + CHART_CARD_CHART_MARGIN.right;
  const height = yPosition + originalHeight + CHART_CARD_CHART_MARGIN.top + CHART_CARD_CHART_MARGIN.bottom;
  svgClone.setAttribute('width', width.toString());
  svgClone.setAttribute('height', height.toString());
  svgClone.setAttribute('viewBox', `-${CHART_CARD_CHART_MARGIN.left} -${CHART_CARD_CHART_MARGIN.top} ${width} ${height}`);

  // 2. Serialize SVG to string
  const serializer = new XMLSerializer();
  let svgString = serializer.serializeToString(svgClone);

  // Embed fonts in SVG for proper rendering in exports
  svgString = handleFontInSVGString(svgString, embeddedFontStyle, fallbackFontFamily);
  switch (exportAs) {
    case 'svg':
      {
        exportAsSVG(svgString, downloadFileName, copyToClipboard, t);
        break;
      }
    case 'png':
      {
        await exportAsPNG({
          svgString,
          width,
          height,
          downloadFileName,
          copyToClipboard,
          t,
          canvas,
          context
        });
        break;
      }
    default:
      assertNever(exportAs);
  }
  sendGTMEvent({
    event: 'chart_export',
    format: exportAs
  });
};
export const calculateStableLeftMargin = ({
  containerWidth,
  baseMargin,
  space,
  otherMargin,
  otherSpace,
  numDataPoints,
  labelsSpace,
  labelsProjections
}: {
  containerWidth: number;
  baseMargin: number;
  space: number;
  otherMargin: number;
  otherSpace: number;
  numDataPoints: number;
  labelsSpace: number;
  labelsProjections: number[] | undefined;
}): {
  leftMargin: number;
  visibleLabels?: boolean[];
} => {
  if (containerWidth === 0 || numDataPoints === 0 || labelsProjections == null || labelsProjections.length === 0) {
    return {
      leftMargin: baseMargin,
      visibleLabels: labelsProjections?.map<boolean>(() => true)
    };
  }
  const visibleLabels: boolean[] = labelsProjections.map<boolean>(() => true);
  const availableSpace = containerWidth - space - otherMargin - otherSpace;
  let stableMargin = 0;
  let previousLabelPosition = -availableSpace;
  let previousLabelIndex = 0;
  const lastLabelIndex = labelsProjections.length - 1;
  for (const [index, projection] of labelsProjections.entries()) {
    // the nth label center is at half of the nth category width
    const k = (index + 0.5) / numDataPoints;
    const labelPosition = availableSpace * k;
    if (labelPosition < previousLabelPosition + labelsSpace) {
      if (index === lastLabelIndex && lastLabelIndex !== 0) {
        // Display the last label if possible
        visibleLabels[previousLabelIndex] = false;
      } else {
        visibleLabels[index] = false;
        continue;
      }
    }
    previousLabelPosition = labelPosition;
    previousLabelIndex = index;

    // Solve the equation:
    // protrusion = projection - labelCenter
    // protrusion = projection - (drawableWidth * k)
    // protrusion = projection - (containerWidth - margin - space - otherMargin - otherSpace) * k
    // Considering that we need a margin that zeroes the protrusion with a baseMargin on top, we have:
    // margin = projection - (containerWidth - margin - space - otherMargin - otherSpace) * k + baseMargin
    // So, to solve for margin:
    // margin = projection - containerWidth * k + margin * k + space * k + otherMargin * k + otherSpace * k + baseMargin
    // margin - margin * k = projection - containerWidth * k + space * k + otherMargin * k + otherSpace * k + baseMargin
    // margin * (1 - k) = projection - (containerWidth - space - otherMargin - otherSpace) * k + baseMargin
    // margin = (projection - (containerWidth - space - otherMargin - otherSpace) * k + baseMargin) / (1 - k)

    stableMargin = Math.max(stableMargin, (projection - (containerWidth - space - otherMargin - otherSpace) * k + baseMargin) / (1 - k));
  }
  return {
    leftMargin: Math.max(Math.ceil(stableMargin) - space, baseMargin),
    visibleLabels
  };
};
export function getNumberValue(value: string | number, percentBase: number): number;
export function getNumberValue(value: string | number | undefined | null, percentBase: number): number | undefined;
export function getNumberValue(value: string | number | undefined | null, percentBase: number): number | undefined {
  if (value == null) {
    return undefined;
  }
  if (typeof value === 'string') {
    if (value.endsWith('%')) {
      return Number.parseFloat(value) / 100 * percentBase;
    }
    return Number.parseFloat(value);
  }
  return value;
}
export const getColorWithShadeOffset = (color: string | undefined, shadeOffset: number | undefined, mode: ThemeMode): string | undefined => {
  if (color == null || shadeOffset == null) {
    return color;
  }
  return fadeColor(color, shadeOffset, mode);
};